import React, { memo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Form, Formik, FormikProps } from "formik";
import moment from "moment";
import Table from "../../ui-components/table/table";
import { TransactionMessage, TransactionMessageProcessingStatus, TransactionMessagesFilters, TransactionMessagesFiltersSchema, TransactionMessagesRequest } from "../../app/data/transaction-monitor/models";
import { TransactionMessageReprocessState, TransactionMessagesState } from "../../slices/transaction-monitor/transaction-messages/TransactionMessagesState";
import { getTransactionMessages, transactionMessagesSelector } from "../../slices/transaction-monitor/transaction-messages/transactionMessagesSlice";
import "./transactionMonitor.scss";
import ContentContainer from "../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import dateFormats from "../../app/data/common/dateFormats";
import { getTransactionMessagesColumns } from "./message/transactionMessagesColumns";
import XGSFormInput from "../../ui-components/form/input/xgsFormInput";
import { LabelModes } from "../../ui-components/molecules/labeled-inputs/labeledInput";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import LabeledDateRangeInput from "../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import TransactionDefinitionsState from "../../slices/transaction-monitor/transaction-definitions/TransactionDefinitionsState";
import { getTransactionDefinitions, transactionDefinitionsSelector } from "../../slices/transaction-monitor/transaction-definitions/transactionDefinitionsSlice";
import { XGSSelectOption } from "../../ui-components/xgs-select/xgsSelect";
import LabeledSelectInput from "../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import TransactionMessageDetails from "./message/message-details/transactionMessageDetails";
import TransactionMessageSubmitReprocess from "./message/submit-reprocess/transactionMessageSubmitReprocess";
import ReloadButton from "../../ui-components/reload-button/reloadButton";
import { transactionMessageReprocessSelector } from "../../slices/transaction-monitor/transaction-messages/transactionMessageReprocessSlice";

const initialFormValues = {
  reference: "",
  scopeId: "",
  messageTypeId: "",
  processingStatus: "",
  flowId: "",
  createdAtFrom: "",
  createdAtTo: ""
};

const TransactionMonitor: React.FC<{}> = memo(() => {
  const dispatch = useDispatch();
  const [filterReference, setFilterReference] = useState<string | null | undefined>(null);
  const [filterScope, setFilterScope] = useState<XGSSelectOption | null | undefined>(null);
  const [filterType, setFilterType] = useState<XGSSelectOption | null | undefined>(null);
  const [filterFlow, setFilterFlow] = useState<XGSSelectOption | null | undefined>(null);
  const [filterStatus, setFilterStatus] = useState<XGSSelectOption | null | undefined>(null);
  const [filterCreatedAtFrom, setFilterCreatedAtFrom] = useState<string | null | undefined>(null);
  const [filterCreatedAtTo, setFilterCreatedAtTo] = useState<string | null | undefined>(null);
  const [selectTransactionMessage, setSelectedTransactionMessage] = useState<TransactionMessage | null>(null);
  const [isShowSubmitReprocess, setIsShowSubmitReprocess] = useState<boolean>(false);

  const formRef = React.useRef<FormikProps<TransactionMessagesFilters>>(null);

  const transactionMessgesState: TransactionMessagesState = useSelector(transactionMessagesSelector);
  const transactionDefinitionsState: TransactionDefinitionsState = useSelector(transactionDefinitionsSelector);
  const transactionMessageReprocessState: TransactionMessageReprocessState = useSelector(transactionMessageReprocessSelector);

  useEffect(() => {
    onSubmit({});
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!transactionDefinitionsState.loaded && !transactionDefinitionsState.loading) {
      dispatch(getTransactionDefinitions());
    }
  }, [dispatch, transactionDefinitionsState])

  const getScopeOptions = () => {
    return (
      transactionDefinitionsState.scopes?.map(
        (scope): XGSSelectOption => ({
          label: scope.name,
          value: scope.id,
        })
      ) || []
    );
  };

  const getTypeOptions = () => {
    return (
      transactionDefinitionsState.types?.map(
        (type): XGSSelectOption => ({
          label: type.name,
          value: type.id,
        })
      ) || []
    );
  };

  const getFlowOptions = () => {
    return (
      transactionDefinitionsState.flows?.map(
        (flow): XGSSelectOption => ({
          label: flow.name,
          value: flow.id,
        })
      ) || []
    );
  };

  const getStatusOptions = () => {
    return (
      Object.values(TransactionMessageProcessingStatus).map(
        (status): XGSSelectOption => ({
          label: status,
          value: status,
        })
      ) || []
    );
  };

  const getFiltersRequest = (
    reference: string | null | undefined,
    scopeId: string | null | undefined,
    messageTypeId: string | null | undefined,
    processingStatus: string | null | undefined,
    flowId: string | null | undefined,
    createdAtFrom: string | null | undefined,
    createdAtTo: string | null | undefined
  ): TransactionMessagesRequest => {
    let request: TransactionMessagesRequest = {};
    if (reference) request.reference = reference;
    if (scopeId) request.scopeId = scopeId;
    if (messageTypeId) request.messageTypeId = messageTypeId;
    if (processingStatus) request.processingStatus = processingStatus;
    if (flowId) request.flowId = flowId;
    if (createdAtFrom) request.createdAtFrom = moment(createdAtFrom).format(dateFormats.apiDate);
    if (createdAtTo) request.createdAtTo = moment(createdAtTo).format(dateFormats.apiDate);
    return request;
  }

  const clearFilters = () => {
    formRef.current?.resetForm();
    setFilterReference(null);
    setFilterCreatedAtFrom(null);
    setFilterCreatedAtTo(null);
    setFilterScope(null);
    setFilterType(null);
    setFilterFlow(null);
    setFilterStatus(null);
  }

  const onSubmit = (data: TransactionMessagesFilters) => {
    let requestData: TransactionMessagesRequest = getFiltersRequest(
      data.reference,
      data.scopeId,
      data.messageTypeId,
      data.processingStatus,
      data.flowId,
      data.createdAtFrom,
      data.createdAtTo
    );
    dispatch(getTransactionMessages(
      requestData,
      (error) => { toast.error(error) }
    ));
  };

  const onListInfiniteScroll = () => {
    if (!transactionMessgesState.hasNextPage) return;
    let requestData: TransactionMessagesRequest = getFiltersRequest(
      filterReference,
      (filterScope && filterScope.value) ? filterScope.value.toString() : null,
      (filterType && filterType.value) ? filterType.value.toString() : null,
      (filterStatus && filterStatus.value) ? filterStatus.value.toString() : null,
      (filterFlow && filterFlow.value) ? filterFlow.value.toString() : null,
      filterCreatedAtFrom,
      filterCreatedAtTo
    );
    requestData.cursor = transactionMessgesState.nextCursor;
    dispatch(getTransactionMessages(
      requestData,
      (error) => { toast.error(error) }
    ));
  }

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ 'full-width': true }}
      titleComponent={
        <ContentContainerToolbar
          title="Transactions Monitor"
          mods={{ "full-width": true }}
        >
          <ReloadButton
            title="Message Reprocess"
            spinner={transactionMessageReprocessState.loading}
            disabled={transactionMessageReprocessState.loading}
            onClick={() => setIsShowSubmitReprocess(true)}
          />
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-transaction-monitor">
        <div className="xgs-list__controls">
          <div className="xgs-list__controls__search">
            <Formik
              initialValues={initialFormValues}
              onSubmit={onSubmit}
              validationSchema={TransactionMessagesFiltersSchema}
              innerRef={formRef}
            >
              {(props: FormikProps<TransactionMessagesFilters>) => (
                <Form className="xgs-list__controls__search-form">
                  <XGSFormInput
                    type="text"
                    name="reference"
                    label="References"
                    placeholder="Search any Reference or ID"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    onChange={(e) => {
                      props.setFieldValue("reference", e?.currentTarget?.value || undefined);
                      setFilterReference(e?.currentTarget?.value);
                    }
                    }
                  />
                  <LabeledSelectInput
                    onValueChange={(value) => {
                      props.setFieldValue("scopeId", value?.value);
                      setFilterScope(value);
                    }}
                    value={filterScope}
                    options={getScopeOptions()}
                    label="Scope"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    isClearable
                  />
                  <LabeledSelectInput
                    onValueChange={(value) => {
                      props.setFieldValue("messageTypeId", value?.value);
                      setFilterType(value || undefined);
                    }}
                    value={filterType}
                    options={getTypeOptions()}
                    label="Message Type"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    isClearable
                  />
                  <LabeledSelectInput
                    onValueChange={(value) => {
                      props.setFieldValue("processingStatus", value?.value);
                      setFilterStatus(value || undefined);
                    }}
                    value={filterStatus}
                    options={getStatusOptions()}
                    label="Processing Status"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    isClearable
                  />
                  <LabeledSelectInput
                    onValueChange={(value) => {
                      props.setFieldValue("flowId", value?.value);
                      setFilterFlow(value || undefined);
                    }}
                    value={filterFlow}
                    options={getFlowOptions()}
                    label="Flow"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    isClearable
                  />
                  <LabeledDateRangeInput
                    label="Date range"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    start={props.values.createdAtFrom}
                    end={props.values.createdAtTo}
                    onStartChange={(v) => {
                      props.setFieldValue("createdAtFrom", v || undefined);
                      setFilterCreatedAtFrom(v || null);
                    }}
                    onEndChange={(v) => {
                      props.setFieldValue("createdAtTo", v || undefined);
                      setFilterCreatedAtTo(v || null);
                    }}
                  />
                  <Button
                    type="submit"
                    theme={ButtonThemes.blue}
                    spinner={false}
                    className="xgs-item-form__btn"
                    disabled={transactionMessgesState.requestStarted || transactionMessgesState.fetchPortionStarted}
                  >
                    Search
                  </Button>
                  <Button
                    theme={ButtonThemes.gray}
                    onClick={clearFilters}
                    className="xgs-item-form__btn"
                  >
                    Clear Filters
                  </Button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
        <div className="xgs-transaction-monitor__table-wrapper">
          <Table
            isLoading={transactionMessgesState.requestStarted}
            columns={getTransactionMessagesColumns()}
            data={transactionMessgesState.messages}
            cursorPointer={true}
            rowHeight={64}
            noResultsText="No Records available"
            onRowClicked={(row: TransactionMessage) => { setSelectedTransactionMessage(row) }}
            onSelectedRowsChange={null}
            infiniteScroll={true}
            infiniteScrollLoading={transactionMessgesState.fetchPortionStarted}
            infiniteScrollHasNext={transactionMessgesState.hasNextPage}
            onInfiniteScroll={onListInfiniteScroll}
            responsive={true}
            minTableHeight={400}
          />
        </div>
        {(selectTransactionMessage && selectTransactionMessage.id) && (
          <TransactionMessageDetails
            transactionMessage={selectTransactionMessage}
            show={!!selectTransactionMessage?.id}
            onClose={() => setSelectedTransactionMessage(null)}
          />
        )}
      </div>
      <TransactionMessageSubmitReprocess
        show={isShowSubmitReprocess}
        onClose={() => { setIsShowSubmitReprocess(false) }}
      />
    </ContentContainer>
  );
});

export default TransactionMonitor;
